/**
 * Slides live inside the sermon, not beside it.
 *
 * A slide is written at the point in the manuscript where it belongs, in one of
 * three ways — a ```` ```slide ```` block for words that are *only* for the
 * screen, `{slide}` on a heading and `:::slide` around a passage for words that
 * belong on the screen *and* on the page. Exporting the manuscript strips the
 * blocks and the markers; exporting the deck keeps the slides, in document
 * order. There is one source of truth — `documents.content` — and never a
 * second document to keep in sync, which is what sank the previous attempt at
 * this feature.
 *
 * ## Why a fence rather than `---`
 *
 * Marp separates slides with `---`. In a manuscript that sequence is already a
 * thematic break and already a frontmatter delimiter, and the highlighter
 * already paints `-{3,}` as a marker. Overloading it means three meanings for
 * one token and a manuscript export littered with stray rules. `---` belongs in
 * the *generated* Marp file (see `toMarpMarkdown`), never in what you type.
 *
 * A fence, by contrast, is a construct the highlighter already parses safely,
 * and every other markdown tool renders it as a code block — ugly but lossless,
 * so exporting the raw `.md` elsewhere destroys nothing.
 *
 * ## Everything downstream reads this module
 *
 * The previous slide editor parsed its own syntax in three places and the three
 * disagreed. Exports, the deck overview and the editor's slide count all go
 * through `parseSlides` and nothing else knows what a slide block looks like.
 */

/**
 * Fenced blocks, shared with the highlighter.
 *
 * `markdown-highlight.ts` imports this so the colours in the editor and the
 * blocks in the export can never disagree about where a slide starts and ends.
 * Kept here because this is the module that has to *parse* blocks; the
 * highlighter only has to paint them.
 *
 * Groups: 1 indent, 2 opening fence, 3 info string, 4 body, 5 closing indent.
 * The `\2` backreference means the closing fence must match the opening
 * exactly, which is how ```` ````slide ```` can contain a plain ``` fence.
 */
export function fencedBlockExpression(): RegExp {
  return /^( {0,3})(`{3,}|~{3,})([^\n]*)(?:\n([\s\S]*?))?^( {0,3})\2[ \t]*$/gm;
}

/** Does a fence's info string open a slide? */
export function isSlideInfo(info: string): boolean {
  return /^slide(?:\s|$)/i.test(info.trim());
}

/**
 * A heading that is also a slide.
 *
 * The second way to make a slide, and the one that annotates rather than
 * replaces. A section heading is already short enough to *be* the slide, so
 * wrapping it in a block would move the words out of the manuscript — the
 * heading would vanish from the page you read from and from the reading view's
 * contents list. Marking it instead keeps `# The Prodigal Son` exactly where it
 * was and adds a title card to the deck.
 *
 * `{slide}` is a trailing attribute in the Pandoc/kramdown idiom. It is stripped
 * from the manuscript, so it never reaches a reader: `parseSlides` cuts it, and
 * the reading view renders the manuscript rather than the raw source.
 *
 * Whitespace inside the braces is allowed. `{ slide }` looks like it ought to
 * work, and a marker that fails silently because of a space is worse than no
 * marker at all — the heading just quietly never becomes a slide.
 *
 * Groups: 1 hashes and their space, 2 the heading text, 3 the marker with any
 * leading whitespace, 4 trailing whitespace.
 */
export function headingSlideExpression(): RegExp {
  return /^( {0,3}#{1,6}[ \t]+)(.*?)([ \t]*\{[ \t]*slide[ \t]*\})([ \t]*)$/gim;
}

/**
 * A stretch of manuscript that is also a slide.
 *
 * The third way to make one, and the answer to the question the other two
 * leave open: what about a scripture quotation, which belongs *both* on the
 * screen and on the page you read from? A fenced block would take it out of the
 * manuscript, so the reading view would lose the very words the sermon turns
 * on and the only way to keep them would be to type them twice — two copies,
 * one of which is always about to be wrong.
 *
 * `:::slide` wraps content instead of replacing it. Only the two marker lines
 * are cut from the manuscript; everything between them stays exactly where it
 * was, still a blockquote or a list or a paragraph, and the same words are
 * handed to the deck. Same principle as `{slide}` on a heading, applied to the
 * blocks a heading marker cannot reach.
 *
 * `:::` is Pandoc's fenced div. It is the idiom for "this content, with a
 * label", it survives a round-trip through other markdown tools as visible
 * text rather than as something silently swallowed, and unlike `---` it means
 * nothing else in a manuscript.
 *
 * Groups: 1 indent, 2 colons, 3 body, 4 closing indent. `\2` makes the closing
 * run match the opening one, so a `::::slide` container can hold a `:::` of
 * its own.
 */
export function slideContainerExpression(): RegExp {
  return /^( {0,3})(:{3,})[ \t]*slide[ \t]*\n([\s\S]*?)^( {0,3})\2[ \t]*$/gim;
}

/**
 * How a slide wants to be shown.
 *
 * Inferred from content rather than declared in the info string: a slide that
 * is only a heading is a title card, one that is only a quotation is a quote
 * card, and everything else is an ordinary content slide. Deriving it means no
 * new syntax to learn and no way for the declaration to contradict the body.
 */
export type SlideLayout = "title" | "quote" | "content";

export type SlideBlock =
  | { kind: "heading"; text: string; level: number }
  | { kind: "bullet"; text: string; depth: number; ordered: boolean; marker: string }
  | { kind: "quote"; text: string }
  | { kind: "text"; text: string }
  | { kind: "image"; url: string; alt: string };

export interface Slide {
  /** 0-based position in the deck. */
  index: number;
  /** First heading in the block, or the first line of prose if there is none. */
  title: string;
  /** Markdown source of the block, fence lines removed and dedented. */
  body: string;
  /** Parsed body, for the renderers. */
  blocks: SlideBlock[];
  layout: SlideLayout;
  /** Offsets of the whole block in the source, fence lines included. */
  start: number;
  end: number;
}

export interface ParsedSlides {
  /** The source with every slide block removed. What the pulpit copy prints. */
  manuscript: string;
  slides: Slide[];
}

/** Remove up to `indent` leading spaces from each line, tabs counting as one. */
function dedent(text: string, indent: number): string {
  if (indent <= 0) return text;
  return text
    .split("\n")
    .map((line) => {
      let cut = 0;
      while (cut < indent && (line[cut] === " " || line[cut] === "\t")) cut += 1;
      return line.slice(cut);
    })
    .join("\n");
}

const HEADING = /^(#{1,6})[ \t]+(.*)$/;
const BULLET = /^(\s*)([-+*])[ \t]+(.*)$/;
const ORDERED = /^(\s*)(\d{1,9})[.)][ \t]+(.*)$/;
const QUOTE = /^[ \t]*>[ \t]?(.*)$/;
const IMAGE = /^!\[([^\]]*)\]\(([^)\s]+)(?:\s+"[^"]*")?\)$/;

/**
 * Break a slide body into the handful of shapes a slide can hold.
 *
 * Deliberately not a markdown parser. A slide is a few lines of large text, so
 * the renderers need headings, bullets, quotations and images and nothing else.
 * Anything unrecognised falls through as `text`, which is always safe — it is
 * still shown, just without special treatment.
 *
 * Consecutive plain lines join into one paragraph the way markdown would; a
 * blank line ends it.
 */
export function parseSlideBlocks(body: string): SlideBlock[] {
  const blocks: SlideBlock[] = [];
  let paragraph: string[] = [];
  let quote: string[] = [];

  const flushParagraph = () => {
    if (paragraph.length) {
      blocks.push({ kind: "text", text: paragraph.join(" ").trim() });
      paragraph = [];
    }
  };
  const flushQuote = () => {
    if (quote.length) {
      blocks.push({ kind: "quote", text: quote.join(" ").trim() });
      quote = [];
    }
  };
  const flush = () => {
    flushParagraph();
    flushQuote();
  };

  for (const line of body.split("\n")) {
    if (!line.trim()) {
      flush();
      continue;
    }

    const quoted = QUOTE.exec(line);
    if (quoted) {
      flushParagraph();
      quote.push(quoted[1]);
      continue;
    }
    flushQuote();

    const heading = HEADING.exec(line);
    if (heading) {
      flushParagraph();
      blocks.push({ kind: "heading", text: heading[2].trim(), level: heading[1].length });
      continue;
    }

    const image = IMAGE.exec(line.trim());
    if (image) {
      flushParagraph();
      blocks.push({ kind: "image", alt: image[1], url: image[2] });
      continue;
    }

    const ordered = ORDERED.exec(line);
    if (ordered) {
      flushParagraph();
      blocks.push({
        kind: "bullet",
        text: ordered[3].trim(),
        depth: Math.floor(ordered[1].replace(/\t/g, "  ").length / 2),
        ordered: true,
        marker: `${ordered[2]}.`,
      });
      continue;
    }

    const bullet = BULLET.exec(line);
    if (bullet) {
      flushParagraph();
      blocks.push({
        kind: "bullet",
        text: bullet[3].trim(),
        depth: Math.floor(bullet[1].replace(/\t/g, "  ").length / 2),
        ordered: false,
        marker: "•",
      });
      continue;
    }

    paragraph.push(line.trim());
  }

  flush();
  return blocks;
}

/** A subtitle long enough to be a sentence is body copy, not a title card. */
const SUBTITLE_MAX = 48;

function inferLayout(blocks: SlideBlock[]): SlideLayout {
  const substantive = blocks.filter((block) => block.kind !== "image");
  if (!substantive.length) return "content";
  if (substantive.every((block) => block.kind === "quote")) return "quote";
  if (substantive.every((block) => block.kind === "heading")) return "title";

  // A heading with one short line under it — a title and its scripture
  // reference, or a title and its subtitle. By far the commonest opening slide,
  // and treating it as ordinary content left it hanging in the top-left corner
  // of an otherwise empty card.
  const others = substantive.filter((block) => block.kind !== "heading");
  const only = others[0];
  if (
    others.length === 1 &&
    substantive.length > others.length &&
    only.kind === "text" &&
    only.text.length <= SUBTITLE_MAX
  ) {
    return "title";
  }

  return "content";
}

function deriveTitle(blocks: SlideBlock[], index: number): string {
  const heading = blocks.find((block) => block.kind === "heading");
  if (heading) return heading.text;
  const first = blocks.find((block) => block.kind === "quote" || block.kind === "text");
  if (first && "text" in first && first.text) {
    return first.text.length > 60 ? `${first.text.slice(0, 57)}…` : first.text;
  }
  return `Slide ${index + 1}`;
}

/**
 * Split a document into the manuscript and the deck.
 *
 * Pure, and the only function that knows any of the three syntaxes. Three
 * scans: fenced `slide` blocks, which leave the manuscript entirely;
 * `{slide}`-marked headings, which stay put and lose only their marker; and
 * `:::slide` containers, which stay put and lose only their two marker lines.
 * All three are then merged into one deck in document order, because the order
 * slides appear in is the order they will be shown.
 *
 * What is cut from the manuscript is tracked separately from what becomes a
 * slide, because the two are not the same list: a `{slide}` heading inside a
 * container still has to lose its marker, but the container has already
 * claimed those words for the deck and a second card would be a duplicate.
 */
export function parseSlides(source: string): ParsedSlides {
  if (!source) return { manuscript: "", slides: [] };

  /** A slide found in the source, before the three kinds are interleaved. */
  type Found = {
    start: number;
    end: number;
    body: string;
    blocks: SlideBlock[];
  };

  const found: Found[] = [];
  /** Spans to remove from the manuscript: whole blocks, or bare marker lines. */
  const cuts: Array<[number, number]> = [];
  /** Every fenced block, so a marker inside code is not mistaken for one. */
  const fences: Array<[number, number]> = [];
  /** Every container, so the heading scan can tell a duplicate from a slide. */
  const containers: Array<[number, number]> = [];

  const fenceExpression = fencedBlockExpression();
  let fence = fenceExpression.exec(source);
  while (fence) {
    const start = fence.index;
    const end = start + fence[0].length;
    fences.push([start, end]);

    if (isSlideInfo(fence[3] ?? "")) {
      const body = dedent(fence[4] ?? "", fence[1].length).replace(/\s+$/, "");
      found.push({ start, end, body, blocks: parseSlideBlocks(body) });
      cuts.push([start, end]);
    }

    // Zero-length matches would spin here forever.
    if (fence[0].length === 0) fenceExpression.lastIndex += 1;
    fence = fenceExpression.exec(source);
  }

  const containerExpression = slideContainerExpression();
  let container = containerExpression.exec(source);
  while (container) {
    const start = container.index;
    const end = start + container[0].length;
    const inFence = fences.some(([from, to]) => start >= from && start < to);

    if (!inFence) {
      containers.push([start, end]);
      const body = dedent(container[3] ?? "", container[1].length).replace(/\s+$/, "");
      found.push({ start, end, body, blocks: parseSlideBlocks(body) });

      // The two marker lines and nothing else. The words between them are the
      // whole point: they stay in the manuscript, still shaped as whatever
      // markdown the author wrote.
      const bodyStart = source.indexOf("\n", start) + 1;
      const closerStart = source.lastIndexOf("\n", end - 1) + 1;
      cuts.push([start, bodyStart]);
      cuts.push([Math.max(closerStart, bodyStart), end]);
    }

    if (container[0].length === 0) containerExpression.lastIndex += 1;
    container = containerExpression.exec(source);
  }

  const headingExpression = headingSlideExpression();
  let heading = headingExpression.exec(source);
  while (heading) {
    const start = heading.index;
    const inFence = fences.some(([from, to]) => start >= from && start < to);
    const inContainer = containers.some(([from, to]) => start >= from && start < to);

    if (!inFence) {
      const end = start + heading[0].length;
      const textEnd = start + heading[1].length + heading[2].length;
      // The marker goes either way — it must never reach a reader — but a
      // heading already inside a container is already on that container's
      // slide, so marking it again adds nothing but a duplicate card.
      cuts.push([textEnd, textEnd + heading[3].length + heading[4].length]);

      if (!inContainer) {
        // Normalised to a level-1 heading: a slide's title is its title,
        // whatever depth the section sat at in the manuscript.
        const body = `# ${heading[2].trim()}`;
        found.push({ start, end, body, blocks: parseSlideBlocks(body) });
      }
    }

    if (heading[0].length === 0) headingExpression.lastIndex += 1;
    heading = headingExpression.exec(source);
  }

  found.sort((left, right) => left.start - right.start);

  const slides: Slide[] = found.map((item, index) => ({
    index,
    title: deriveTitle(item.blocks, index),
    body: item.body,
    blocks: item.blocks,
    layout: inferLayout(item.blocks),
    start: item.start,
    end: item.end,
  }));

  // Cuts are applied in source order with a cursor that only moves forward, so
  // a marker nested inside a span that was already removed cannot rewind it and
  // duplicate the text in between.
  const kept: string[] = [];
  let cursor = 0;
  for (const [from, to] of cuts.sort((left, right) => left[0] - right[0])) {
    if (to <= from || from < cursor) continue;
    kept.push(source.slice(cursor, from));
    cursor = to;
  }
  kept.push(source.slice(cursor));

  // Cutting a block out of its own paragraph gap leaves four newlines behind.
  // Collapse to the one blank line the author would have typed.
  const manuscript = kept
    .join("")
    .replace(/\n{3,}/g, "\n\n")
    .trim();

  return { manuscript, slides };
}

/**
 * Serialise the deck as Marp markdown.
 *
 * This is the one place `---` is correct: it is a generated artefact for
 * another tool, not something anyone has to type or read in the editor.
 *
 * `size` carries the chosen aspect ratio through, so a deck set up for a 4:3
 * projector arrives in Marp the same shape it left here rather than reverting
 * to widescreen.
 */
export function toMarpMarkdown(slides: Slide[], title: string, size = "16:9"): string {
  const frontmatter = [
    "---",
    "marp: true",
    "theme: default",
    "paginate: true",
    `size: ${size}`,
    `title: ${title.replace(/\n/g, " ")}`,
    "---",
  ].join("\n");

  return `${frontmatter}\n\n${slides.map((slide) => slide.body).join("\n\n---\n\n")}\n`;
}
